/**
 * Same-origin API client. Session cookie rides along automatically;
 * mutations attach the CSRF token minted by GET /api/auth/csrf.
 */
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

let csrfToken = "";

export function setCsrfToken(t: string) {
  csrfToken = t;
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init.headers as Record<string, string>),
  };
  if (init.body && !headers["Content-Type"]) headers["Content-Type"] = "application/json";
  if (method !== "GET" && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const r = await fetch(path, { ...init, headers, credentials: "same-origin" });
  if (r.status === 401 && !path.startsWith("/api/auth/login")) {
    // session expired mid-use → bounce to login screen
    window.dispatchEvent(new Event("nx:unauthorized"));
  }
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try {
      const body = await r.json();
      msg = body.detail ?? body.message ?? msg;
      if (Array.isArray(msg)) msg = msg.map((m: { msg?: string }) => m.msg).join("; ");
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(r.status, String(msg));
  }
  if (r.status === 204) return undefined as T;
  return (await r.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  postRaw: <T>(path: string, body: string, contentType = "application/octet-stream") =>
    request<T>(path, { method: "POST", body, headers: { "Content-Type": contentType } }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PUT", body: JSON.stringify(body) }),
  del: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};

// ---- typed endpoints -------------------------------------------------------
export interface User {
  id: string;
  email: string;
  name: string;
  role: "owner" | "operator" | "viewer";
  totp_enabled: boolean;
  is_active: boolean;
}

export interface Application {
  id: string;
  name: string;
  bw_service: string;
  server_names: string[];
  profile: string;
  status: string;
  detect_only: boolean;
  origin_scheme: string;
  origin_host: string;
  origin_port: number;
  origin_tls_verify: boolean;
  origin_sni: string;
  cert_method: string;
  variables: Record<string, string>;
  disabled_controls: Record<string, string>;
  protection: string;
  nx: { state: string; desired: string[]; effective: string[];
        missing_plugins: string[] } | null;
  coverage_note: string;
  created_at: string;
}

export interface Revision {
  id: number;
  app_id: string | null;
  seq: number;
  status: string;
  error: string;
  convergence: Record<string, unknown>;
  idempotency_key: string;
  is_rollback: boolean;
  created_by: string;
  created_at: string;
  applied_at: string | null;
}

export interface SecEvent {
  id: number;
  ts: string;
  service: string;
  app_id: string | null;
  client_ip: string;
  country: string;
  method: string;
  host: string;
  path: string;
  status_code: number;
  action: string;
  control: string;
  rule_id: string;
  inspected: boolean;
  meta: Record<string, unknown>;
}

export interface PluginInfo {
  id: string;
  name: string;
  version: string;
  source: string;
  license_class: string;
  availability: string;
  enabled: boolean;
  installed_in_bw: boolean;
  notes: string;
  meta: {
    description?: string;
    classification?: string;
    external_dependencies?: string[];
    data_sharing?: string;
    operational_state?: string;
    blocker?: string | null;
    settings_count?: number;
    stream_support?: string;
  };
}

export interface Job {
  id: string;
  type: string;
  status: string;
  attempts: number;
  progress: Record<string, unknown>;
  error: string;
  created_at: string;
  idempotency_key: string;
}

export interface Overview {
  nodes: { total: number; healthy: number; items: { hostname: string; status: string; last_seen_at: string | null }[] };
  apps: { total: number; active: number; detect: number; draft: number };
  counters_24h: { allowed: number; challenged: number; blocked: number; errors: number; uninspected: number };
  distinct_requests_24h: number;
  per_control_24h: Record<string, number>;
  data_freshness: { last_event_at: string | null; collection_gap_suspected: boolean };
}

export interface AuditRow {
  id: number;
  at: string;
  actor: string;
  action: string;
  target: string;
  ip: string;
  detail: Record<string, unknown>;
  hash: string;
}

export interface OpsHealth {
  status: "ok" | "degraded" | "down";
  reasons: string[];
  workers: {
    worker_id: string;
    status: string;
    last_seen_at: string | null;
    last_completed_at: string | null;
    completed_total: number;
    queue_depth: number;
  }[];
  queue: {
    queued: number;
    leased: number;
    runnable: number;
    oldest_runnable_at: string | null;
    oldest_runnable_age_s: number | null;
    failed_24h: number;
  };
  at: string;
}

export interface Exception_ {
  id: string;
  app_id: string;
  status: string;
  control: string;
  rule_id: string;
  scope: Record<string, string>;
  reason: string;
  created_by: string;
  created_at: string;
  expires_at: string;
}

export interface Ban {
  ip: string;
  service?: string | null;
  exp?: number;
  reason?: string;
  created_at?: string;
  [k: string]: unknown;
}

export interface Capabilities {
  product?: string;
  version?: string;
  [k: string]: unknown;
}

// ---- plugin catalog / lifecycle -------------------------------------------
export interface CatalogItem {
  id: string;
  name: string;
  source: "core" | "pro" | "external";
  version: string;
  license: string;
  page: string;
  settings_count: number;
  capabilities: {
    infra?: boolean;
    credentials?: string;
    external_service?: string;
    data_sharing?: string;
    entitlement?: string;
    notes?: string;
  };
  capability_state: string;
  capability_reason: string;
  state: {
    lifecycle: string;
    enabled: boolean;
    installed: boolean;
    pending_restart: boolean;
    disabled_reason: string;
    last_error: string;
  } | null;
}

export interface SettingSpec {
  context: string;
  default: string;
  help: string;
  label: string;
  regex: string;
  type: string;
  secret: boolean;
}

export interface PluginDetail extends Omit<CatalogItem, "state" | "capability_state" | "capability_reason"> {
  description?: string;
  settings: Record<string, SettingSpec>;
  provenance?: string;
  state: {
    lifecycle: string;
    enabled: boolean;
    installed: boolean;
    pending_restart: boolean;
    entitled_pro: boolean;
    disabled_reason: string;
    last_error: string;
    settings: Record<string, string>;
  } | null;
  app_configs: { app_id: string; app: string; enabled: boolean; settings: Record<string, string>; reason: string }[];
  artifacts: { id: string; version: string; sha256: string; signature_status: string; approved_by: string; status: string; installed_at: string | null }[];
}

// ---- update center ---------------------------------------------------------
export interface UpdatePolicy {
  mode: "notify" | "stage" | "scheduled" | "automatic";
  allowed_components: string[];
  version_range: string;
  window: string;
  timezone: string;
  updated_by: string;
}

export interface UpdateItem {
  id: string;
  channel: string;
  component: string;
  version: string;
  current_version: string;
  source: string;
  digest: string;
  risk: string;
  requires_review: boolean;
  review_reasons: string[];
  status: string;
  discovered_at: string;
  detail: Record<string, unknown>;
}

export interface UpdateJob {
  id: string;
  item_id: string;
  status: string;
  requested_by: string;
  approved_by: string;
  scheduled_for: string | null;
  backup_ref: string;
  recovery_point: string;
  progress: { at: string; to: string; step: string; note: string }[];
  error: string;
  created_at: string;
  finished_at: string | null;
}

// ---- admin ------------------------------------------------------------------
export interface CertRow {
  domain: string;
  app: string;
  app_id: string;
  method: string;
  status: string;
}

export interface SessionRow {
  id: number;
  user: string;
  ip: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
}

export interface NotificationRow {
  id: number;
  at: string;
  severity: string;
  kind: string;
  title: string;
  detail: Record<string, unknown>;
  read_by: string[];
}

export interface DriftReport {
  nodes: { hostname: string; reachable?: boolean; last_seen_at?: string; status?: string; error?: string }[];
  apps: { app_id: string; service: string; status: string; changed: Record<string, unknown>; missing: Record<string, unknown>; unmanaged: Record<string, unknown> }[];
}

export interface BackupRow {
  id: string;
  path: string;
  size_bytes: number;
  contents: string[];
  sha256: string;
  created_at_fs: string | null;
  seen_at: string;
}

export interface PolicyProfile {
  id: string;
  name: string;
  description: string;
  variables: Record<string, string>;
  custom: boolean;
}

export interface NxModule {
  id: string;                 // nx.<name>
  version: string;
  title: string;
  description: string;
  provides: string[];
  permissions: string[];
  depends_on: string[];
  conflicts: string[];
  mandatory: boolean;
  needs_executor: boolean;
  saved: Record<string, unknown>;
  enabled: boolean;
  state: string;
  effective: { ok: boolean; detail: string } | null;
  last_error: string;
}

// ---- M2 native features ----------------------------------------------------
export interface PageTemplate {
  id: string; app_id: string | null; kind: "block" | "maintenance";
  version: number; active: boolean; created_by: string; created_at: string;
  vars: string[];
}
export interface MaintenanceState {
  app_id: string; enabled: boolean; until: string | null;
  allow_paths: string[]; allow_cidrs: string[]; reason: string;
  updated_by: string; updated_at: string;
}
export interface MigrationEnvelope {
  format: string; format_version: number; exported_at: string;
  source: { project: string };
  applications: Record<string, unknown>[];
  policy_profiles: Record<string, unknown>[];
  secret_refs: string[];
}
export interface FPExplain {
  event_id: number; rule_id: string; control: string;
  rule_name: string; rule_detail: string; matched_data: string;
  request: { method: string; host: string; path: string; client_ip: string };
  candidate_scope: Record<string, string>;
}
export interface CertAppStatus {
  app_id: string; name: string; cert_method: string; wildcards: string[];
  state: { checked_at: string | null; cert_not_after: string | null;
           days_left: number | null; renewal_status: string;
           error: string } | null;
}

// ---- M3 native features ----------------------------------------------------
export interface LbMember {
  id: string; host: string; port: number; weight: number;
  enabled: boolean; draining: boolean; state: string;
  probe_oks: number; probe_fails: number; last_error: string;
  last_checked_at: string | null;
}
export interface LbPool {
  enabled: boolean; algorithm: string;
  connect_timeout_s: number; read_timeout_s: number; send_timeout_s: number;
  max_fails: number; fail_timeout_s: number;
  health_path: string; probe_timeout_s: number;
  down_after: number; recover_after: number;
  tls_scheme: string; tls_sni: string; tls_verify: boolean;
  updated_by: string; updated_at: string;
}
export interface CachePolicy {
  configured: boolean; enabled?: boolean;
  ttl_s?: number; zone_mb?: number; max_mb?: number;
  prefixes?: string[]; vary_attrs?: string[]; epoch?: number;
  purged_at?: string | null; updated_by?: string; updated_at?: string;
}
export interface OasStatus {
  app_id: string; configured: boolean;
  mode?: string; spec_name?: string; spec_sha256?: string;
  spec_format?: string; openapi_version?: string; routes?: number;
  unsupported?: { where: string; feature: string }[];
  exceptions?: { method: string; path: string }[];
  bypass_cidrs?: string[];
  applied?: boolean; uploaded_by?: string; uploaded_at?: string | null;
}

// ---- M4 native features ----------------------------------------------------
export interface AuthPolicy {
  app_id: string; configured: boolean; enabled: boolean;
  provider_ids: string[]; session_ttl_s: number;
  exempt_paths: string[]; require_groups: string[];
  recovery_cidrs: string[]; recovery_token_ref: string;
  active_sessions: number; updated_by: string;
}
export interface AppSession {
  id: number; sub: string; iss: string; email: string; name: string;
  groups: string[]; created_at: string; expires_at: string;
  revoked: boolean; expired: boolean; ip: string;
}
export interface RatePolicy {
  id: string; app_id: string; name: string; enabled: boolean;
  mode: string; paths: string[]; methods: string[]; key: string;
  trusted_cidrs: string[]; bypass_cidrs: string[];
  rate: number; window_s: number; burst: number;
  expires_at: string | null; expired: boolean; created_by: string;
}
export interface RateList {
  app_id: string; policies: RatePolicy[]; scope: string;
}
